import React from 'react';
import { motion } from 'framer-motion';
import { FaCamera, FaHeart, FaLink, FaRedoAlt, FaShareAlt, FaSignature } from 'react-icons/fa';
import { useTheme } from '../themeConfig';
import { useFeedback } from '../context/FeedbackContext';

const findModuleId = (modules, types) => {
  const acceptedTypes = Array.isArray(types) ? types : [types];
  const module = modules.find((item) => acceptedTypes.includes(item.type));
  return module?.id || module?._localId || '';
};

const AfterglowExperience = ({
  modules = [],
  ownerName,
  sharingSettings = {},
  storyCoachAnswers = {},
  storyAnalysis = {},
  onOpenShareCard,
  onReplayOpening,
}) => {
  const { theme } = useTheme();
  const feedback = useFeedback();
  const allowFullStoryShare = sharingSettings.allowFullStoryShare !== false;
  const allowMomentShare = sharingSettings.allowMomentShare !== false;
  const reactionId = findModuleId(modules, ['polaroid_booth', 'reaction']);
  const guestbookId = findModuleId(modules, 'guestbook');
  const finalLine = storyCoachAnswers.afterglow || storyCoachAnswers.peak || '';
  const canCopyLink = allowFullStoryShare || allowMomentShare;
  const hasGuestbookInvite = typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).has('guestbook');

  const stopPressPropagation = (event) => {
    event.stopPropagation();
  };

  const scrollToModule = (moduleId) => {
    if (!moduleId) return;
    const target = document.querySelector(`[data-module-id="${moduleId}"]`);
    target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const openGuestbook = () => {
    setTimeout(() => scrollToModule(guestbookId), 50);
  };

  const copyLink = async () => {
    await feedback.copyText({
      title: 'Gift Link',
      message: 'Copy this gift link. Anyone with the URL may be able to open it.',
      value: window.location.href,
      successMessage: 'Gift link copied.',
    });
  };

  return (
    <motion.section
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      className="mx-auto mt-16 w-full max-w-2xl px-4"
    >
      <div
        className="lumirae-ds-panel p-7 text-center"
        style={{
          '--lumirae-accent-primary': theme.accent,
          '--lumirae-accent-soft': theme.accentSoft,
          '--lumirae-surface-raised': theme.cardBg,
          '--lumirae-border-default': theme.cardBorder,
          '--lumirae-text-primary': theme.text,
          '--lumirae-text-secondary': theme.secondaryText,
        }}
      >
        <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-[1.5rem] text-white shadow-xl" style={{ backgroundColor: theme.accent }}>
          <FaHeart size={28} />
        </div>
        <div className="lumirae-ds-meta" style={{ color: theme.accent }}>
          Afterglow
        </div>
        <h2 className="mt-3 text-3xl font-black uppercase leading-tight" style={{ color: theme.text }}>
          This is yours now.
        </h2>
        <p className="mx-auto mt-3 max-w-sm text-xs font-bold leading-relaxed" style={{ color: theme.secondaryText }}>
          Take a breath. Keep the feeling, send something back, or open the gift again whenever you want.
        </p>
        {finalLine && (
          <div className="lumirae-ds-soft-note mx-auto mt-5 max-w-md px-5 py-4 text-left">
            <div className="lumirae-ds-meta" style={{ color: theme.accent }}>
              Final Note
            </div>
            <p className="mt-2 text-sm font-black italic leading-relaxed" style={{ color: theme.text }}>
              {finalLine}
            </p>
          </div>
        )}
        <div className="mx-auto mt-5 grid max-w-md grid-cols-3 gap-2">
          <div className="lumirae-ds-list-card px-3 py-4">
            <div className="text-lg font-black" style={{ color: theme.text }}>{storyAnalysis.momentCount || modules.length}</div>
            <div className="lumirae-ds-meta">Moments</div>
          </div>
          <div className="lumirae-ds-list-card px-3 py-4">
            <div className="text-lg font-black" style={{ color: theme.text }}>{storyAnalysis.estimatedMinutes || 1}</div>
            <div className="lumirae-ds-meta">Min Read</div>
          </div>
          <div className="lumirae-ds-list-card px-3 py-4">
            <div className="text-lg font-black" style={{ color: theme.text }}>{sharingSettings.shareMode === 'private' ? 'Off' : 'On'}</div>
            <div className="lumirae-ds-meta">Sharing</div>
          </div>
        </div>

        <div className="mt-7 grid grid-cols-1 gap-3 sm:grid-cols-2">
          {onOpenShareCard && canCopyLink && (
            <button
              type="button"
              onClick={onOpenShareCard}
              onMouseDown={stopPressPropagation}
              onTouchStart={stopPressPropagation}
              className="lumirae-ds-button lumirae-ds-button-primary px-5 py-4 shadow-lg"
            >
              <FaCamera className="mr-2 inline" /> Save Share Card
            </button>
          )}
          {reactionId && (
            <button
              type="button"
              onClick={() => scrollToModule(reactionId)}
              onMouseDown={stopPressPropagation}
              onTouchStart={stopPressPropagation}
              className="lumirae-ds-button lumirae-ds-button-secondary px-5 py-4 shadow-lg"
            >
              <FaHeart className="mr-2 inline" style={{ color: theme.accent }} /> Send Reaction
            </button>
          )}
          {guestbookId && (
            <button
              type="button"
              onClick={openGuestbook}
              onMouseDown={stopPressPropagation}
              onTouchStart={stopPressPropagation}
              className="lumirae-ds-button lumirae-ds-button-secondary px-5 py-4 shadow-lg"
            >
              <FaSignature className="mr-2 inline" style={{ color: theme.accent }} />
              {hasGuestbookInvite ? 'Sign Blessing Wall' : 'View Blessings'}
            </button>
          )}
          {canCopyLink && (
            <button
              type="button"
              onClick={copyLink}
              onMouseDown={stopPressPropagation}
              onTouchStart={stopPressPropagation}
              className="lumirae-ds-button lumirae-ds-button-secondary px-5 py-4 shadow-lg"
            >
              {allowFullStoryShare ? <FaShareAlt className="mr-2 inline" style={{ color: theme.accent }} /> : <FaLink className="mr-2 inline" style={{ color: theme.accent }} />}
              {allowFullStoryShare ? 'Share Gift' : 'Copy Moment'}
            </button>
          )}
          <button
            type="button"
            onClick={onReplayOpening}
            onMouseDown={stopPressPropagation}
            onTouchStart={stopPressPropagation}
            className="lumirae-ds-button lumirae-ds-button-soft px-5 py-4 shadow-lg"
          >
            <FaRedoAlt className="mr-2 inline" /> Open Again
          </button>
        </div>

        {!allowFullStoryShare && !allowMomentShare && (
          <p className="lumirae-ds-meta mt-5">
            Share buttons are off. This does not revoke any link that was already shared.
          </p>
        )}
        {!allowFullStoryShare && allowMomentShare && (
          <p className="lumirae-ds-meta mt-5">
            Full story sharing is off. The closing moment can still be copied.
          </p>
        )}
        {ownerName && (
          <p className="mt-4 text-[9px] font-black uppercase tracking-[0.35em]" style={{ color: theme.accent }}>
            For {ownerName}
          </p>
        )}
      </div>
    </motion.section>
  );
};

export default AfterglowExperience;
